import { Link } from 'react-router-dom';
import { Card } from '@/components/ui';
import { formatPhone } from '@/lib/phoneUtils';
import type { CustomerListItem } from '../types';

interface CustomerCardProps {
  customer: CustomerListItem;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function CustomerCard({
  customer,
  onEdit,
  onDelete,
}: CustomerCardProps) {
  // Get primary phone from all_phones array
  const primaryPhone = customer.all_phones?.find((p) => p.is_primary);
  const displayPhone = primaryPhone?.phone_number || customer.phone;

  const formatAddress = () => {
    const parts: string[] = [];
    if (customer.billing_city) {
      let cityLine = customer.billing_city;
      if (customer.billing_province) {
        cityLine += `, ${customer.billing_province}`;
      }
      parts.push(cityLine);
    }
    return parts.join(', ');
  };

  const hasAddress = customer.billing_city || customer.billing_province;

  return (
    <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
      <div className="p-4 flex flex-col h-full">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0 flex-1">
            <Link
              to={`/customers/${customer.id}`}
              className="text-lg font-semibold text-secondary-900 hover:text-primary-600
                         transition-colors block truncate"
            >
              {customer.full_name}
            </Link>
          </div>

          {/* Action Buttons */}
          {(onEdit || onDelete) && (
            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="p-1.5 text-secondary-400 hover:text-primary-600
                             hover:bg-primary-50 rounded transition-colors"
                  title="Edit customer"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              )}
              {onDelete && customer.project_count === 0 && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="p-1.5 text-secondary-400 hover:text-red-600
                             hover:bg-red-50 rounded transition-colors"
                  title="Delete customer"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Contact Info Section */}
        <div className="space-y-2 text-sm flex-1">
          {/* Email */}
          <div className="flex items-center gap-2 min-h-[20px]">
            <svg
              className="w-4 h-4 text-secondary-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            {customer.email ? (
              <a
                href={`mailto:${customer.email}`}
                className="text-primary-600 hover:underline truncate"
              >
                {customer.email}
              </a>
            ) : (
              <span className="text-secondary-400 italic">No email</span>
            )}
          </div>

          {/* Primary Phone */}
          <div className="flex items-center gap-2 min-h-[20px]">
            <svg
              className="w-4 h-4 text-secondary-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            {displayPhone ? (
              <span className="text-secondary-600">{formatPhone(displayPhone)}</span>
            ) : (
              <span className="text-secondary-400 italic">No phone</span>
            )}
          </div>

          {/* Address (City, Province only) */}
          <div className="flex items-center gap-2 min-h-[20px]">
            <svg
              className="w-4 h-4 text-secondary-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {hasAddress ? (
              <span className="text-secondary-600 truncate">{formatAddress()}</span>
            ) : (
              <span className="text-secondary-400 italic">No address</span>
            )}
          </div>
        </div>

        {/* Footer Stats Section */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-secondary-100">
          <div className="flex items-center gap-1.5 text-sm text-secondary-500">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <span>
              {customer.project_count} {customer.project_count === 1 ? 'project' : 'projects'}
            </span>
          </div>
          {customer.phone_count > 1 && (
            <span className="text-xs text-secondary-400">
              +{customer.phone_count - 1} more {customer.phone_count - 1 === 1 ? 'phone' : 'phones'}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
